import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSiteContent } from "@/hooks/useContent";

const FALLBACK_IMG = "https://customer-assets.emergentagent.com/job_87c58432-5d7c-47ab-ad60-b480eb93388c/artifacts/u0qrrif2_IMG-20260410-WA0005.jpg";

function AnimatedCounter({ target, suffix = "", label }) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => Math.round(v));
  const ref = useRef(null);
  const displayRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { animate(count, target, { duration: 2, ease: "easeOut" }); observer.disconnect(); } }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    const sub = rounded.on("change", (v) => { if (displayRef.current) displayRef.current.textContent = v + suffix; });
    return () => { observer.disconnect(); sub(); };
  }, [count, target, suffix, rounded]);
  return (
    <div ref={ref} className="text-center group cursor-pointer">
      <span ref={displayRef} className="font-heading text-3xl md:text-4xl font-extrabold text-white group-hover:text-gg-red transition-colors duration-300">0{suffix}</span>
      <p className="text-zinc-500 text-xs uppercase tracking-widest mt-1 font-heading">{label}</p>
    </div>
  );
}

export default function About() {
  const { content } = useSiteContent();
  const about = content?.about || { name: "Gora Ganguly", bio: "", image: FALLBACK_IMG };
  const qualCount = content?.qualifications?.length || 7;
  const serviceCount = content?.services?.length || 9;

  // Render bio HTML from TipTap (supports <strong>, <em>, <u>, <p>, <ul>)
  const bioHtml = (about.bio || "")
    .replace(/<b>/g, '<strong>').replace(/<\/b>/g, '</strong>')
    .replace(/<strong>/g, '<span class="text-white font-semibold">').replace(/<\/strong>/g, '</span>');
  
  // Split by paragraphs - handle both \n\n and <p> tags
  const hasPTags = bioHtml.includes('<p>');
  const bioParagraphs = hasPTags 
    ? [bioHtml] // Already has <p> tags from TipTap, render as single block
    : bioHtml.split('\n\n').filter(Boolean);

  return (
    <section id="about" className="py-8 md:py-10 relative" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row gap-8 md:gap-10 md:items-start">
          <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="md:w-7/12">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight leading-tight optical-align">
              Hi, I'm <span className="text-gg-red">{about.name}</span>
            </h2>
            <div className="mt-5 space-y-4 text-zinc-400 text-sm leading-relaxed tracking-wide about-bio">
              {hasPTags ? (
                <div dangerouslySetInnerHTML={{ __html: bioHtml }} />
              ) : (
                bioParagraphs.map((p, i) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
                ))
              )}
            </div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.4 }}>
              <Link to="/qualifications" className="inline-flex items-center gap-2 mt-6 text-gg-red hover:text-white text-sm font-heading uppercase tracking-widest transition-colors duration-300 group whitespace-nowrap" data-testid="about-view-credentials">
                View All Credentials <ArrowRight size={14} className="group-hover:translate-x-1.5 transition-transform shrink-0" />
              </Link>
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 40, scale: 0.95 }} whileInView={{ opacity: 1, x: 0, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }} className="md:w-5/12">
            <div className="relative overflow-hidden rounded-xl border border-white/10 img-zoom aspect-[4/5] max-h-[500px]">
              <img src={about.image || FALLBACK_IMG} alt={`${about.name} - Certified Personal Trainer in Bolpur Santiniketan`} className="absolute inset-0 w-full h-full object-cover object-top img-high-contrast" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-gg-black/60 via-transparent to-transparent" />
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="mt-16 grid grid-cols-3 gap-8 border-t border-b border-white/5 py-8">
          <AnimatedCounter target={18} suffix="+" label="Years Experience" />
          <AnimatedCounter target={serviceCount} suffix="+" label="Specializations" />
          <AnimatedCounter target={qualCount} suffix="+" label="Certifications" />
        </motion.div>
      </div>
    </section>
  );
}
